import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/Field'
import { Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { formatDateShort } from '@/lib/dates'
import { formatPhone, pluralise } from '@/lib/format'
import { jobName } from '@/lib/jobs'
import { describeError } from '@/lib/supabase'
import { useBoard, type BoardJob } from '@/features/pipeline/queries'
import { Stars } from '@/features/team/TeamPage'
import { SERVICE_LABEL, useDeleteVendor, useSaveVendor, useVendors, type Vendor } from './queries'

/**
 * Vendors: everyone else she has worked with at weddings (planners, DJs,
 * florists, venues ...), with what they do, how to reach them, the weddings,
 * and her rating. Starred as preferred, it can become a list for her couples.
 * Kept basic for now (27 Oct 2026).
 */
export function VendorsPage() {
  const vendors = useVendors()
  const board = useBoard()
  const [service, setService] = useState('')
  const [onlyPreferred, setOnlyPreferred] = useState(false)
  const [editing, setEditing] = useState<Vendor | 'new' | null>(null)

  const jobsById = useMemo(() => new Map((board.data ?? []).map((job) => [job.id, job])), [board.data])
  const rows = (vendors.data ?? []).filter((vendor) => (!service || vendor.service === service) && (!onlyPreferred || vendor.preferred))

  return (
    <div className="pt-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-[length:var(--text-sm)] text-taupe">
          {vendors.data ? `${pluralise(rows.length, 'vendor')} you have worked with.` : 'Vendors you have worked with.'}
        </p>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Add a vendor
        </Button>
      </div>

      {editing ? (
        <VendorForm
          key={editing === 'new' ? 'new' : editing.id}
          vendor={editing === 'new' ? null : editing}
          jobs={board.data ?? []}
          onDone={() => setEditing(null)}
        />
      ) : null}

      <div className="mb-3 flex flex-wrap items-end gap-4">
        <Field label="What they do" className="w-56">
          <Select value={service} onChange={(event) => setService(event.target.value)}>
            <option value="">Everyone</option>
            {Object.entries(SERVICE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox label="Only preferred vendors" checked={onlyPreferred} onChange={(event) => setOnlyPreferred(event.target.checked)} className="pb-2" />
      </div>

      {vendors.isPending ? (
        <Loading label="Loading vendors" />
      ) : vendors.isError ? (
        <ErrorState title="Vendors could not load" detail={describeError(vendors.error)} onRetry={() => void vendors.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={vendors.data.length ? 'Nobody matches that' : 'No vendors yet'}
          body="Add the planners, DJs, florists, venues and others you work with, and rate them as you go."
          action={{ label: 'Add a vendor', onClick: () => setEditing('new') }}
        />
      ) : (
        <div className="card overflow-x-auto px-2">
          <table className="ledger min-w-[56rem]">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">What they do</th>
                <th className="label">Email</th>
                <th className="label">Phone</th>
                <th className="label">Weddings</th>
                <th className="label">Rating</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((vendor) => (
                <tr key={vendor.id}>
                  <td>
                    <button type="button" className="text-left font-semibold hover:underline" onClick={() => setEditing(vendor)}>
                      {vendor.name}
                    </button>{' '}
                    {vendor.preferred ? <Badge tone="sage">Preferred</Badge> : null}
                  </td>
                  <td className="text-taupe">{SERVICE_LABEL[vendor.service] ?? vendor.service}</td>
                  <td className="text-taupe">{vendor.email ? <a href={`mailto:${vendor.email}`} className="hover:underline">{vendor.email}</a> : '—'}</td>
                  <td className="whitespace-nowrap text-taupe">{vendor.phone ? formatPhone(vendor.phone) : '—'}</td>
                  <td className="text-taupe">
                    {vendor.jobs.length
                      ? vendor.jobs
                          .map((link) => jobsById.get(link.job_id))
                          .filter((job): job is BoardJob => Boolean(job))
                          .map((job, index) => (
                            <span key={job.id}>
                              {index ? ', ' : ''}
                              <Link to={`/weddings/${job.id}`} className="hover:underline">
                                {jobName(job)}
                              </Link>
                            </span>
                          ))
                      : '—'}
                  </td>
                  <td>
                    <Stars value={vendor.rating} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function VendorForm({ vendor, jobs, onDone }: { vendor: Vendor | null; jobs: BoardJob[]; onDone: () => void }) {
  const save = useSaveVendor()
  const remove = useDeleteVendor()
  const [form, setForm] = useState({
    name: vendor?.name ?? '',
    service: vendor?.service ?? 'planner',
    email: vendor?.email ?? '',
    phone: vendor?.phone ?? '',
    rating: vendor?.rating === null || vendor?.rating === undefined ? '' : String(vendor.rating),
    preferred: vendor?.preferred ?? false,
    notes: vendor?.notes ?? '',
  })
  const [jobIds, setJobIds] = useState<string[]>(vendor?.jobs.map((link) => link.job_id) ?? [])
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }))
  const sorted = [...jobs].sort((a, b) => b.event_date.localeCompare(a.event_date))

  return (
    <Section title={vendor ? vendor.name : 'Add a vendor'}>
      <form
        className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!form.name.trim()) return
          save.mutate(
            {
              ...(vendor ? { id: vendor.id } : {}),
              name: form.name.trim(),
              service: form.service,
              email: form.email.trim() || null,
              phone: form.phone.trim() || null,
              rating: form.rating ? Number(form.rating) : null,
              preferred: form.preferred,
              notes: form.notes.trim() || null,
              jobIds,
            },
            { onSuccess: onDone },
          )
        }}
      >
        <Field label="Name">
          <Input value={form.name} onChange={(event) => set('name', event.target.value)} placeholder="Person or business" />
        </Field>
        <Field label="What they do">
          <Select value={form.service} onChange={(event) => set('service', event.target.value)}>
            {Object.entries(SERVICE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Your rating">
          <Select value={form.rating} onChange={(event) => set('rating', event.target.value)}>
            <option value="">Not rated</option>
            {[5, 4, 3, 2, 1].map((value) => (
              <option key={value} value={value}>
                {'★'.repeat(value)} ({value})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(event) => set('email', event.target.value)} />
        </Field>
        <Field label="Phone">
          <Input type="tel" value={form.phone} onChange={(event) => set('phone', event.target.value)} />
        </Field>
        <div className="flex items-end pb-2">
          <Checkbox label="Preferred vendor" description="One you'd recommend to your couples." checked={form.preferred} onChange={(event) => set('preferred', event.target.checked)} />
        </div>
        <Field label="Weddings you worked on together" className="sm:col-span-2 lg:col-span-3">
          <div className="flex flex-wrap items-center gap-2">
            {jobIds.map((id) => {
              const job = jobs.find((candidate) => candidate.id === id)
              return (
                <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[var(--color-wash-strong)] py-0.5 pr-1 pl-2.5 text-[length:var(--text-xs)]">
                  {job ? `${jobName(job)} · ${formatDateShort(job.event_date)}` : 'A removed wedding'}
                  <button type="button" aria-label="Remove" className="rounded-full px-1.5 hover:bg-[var(--color-rule)]" onClick={() => setJobIds(jobIds.filter((value) => value !== id))}>
                    ✕
                  </button>
                </span>
              )
            })}
            <Select
              aria-label="Add a wedding"
              value=""
              onChange={(event) => event.target.value && setJobIds([...jobIds, event.target.value])}
              className="w-64"
            >
              <option value="">+ Add a wedding</option>
              {sorted
                .filter((job) => !jobIds.includes(job.id))
                .map((job) => (
                  <option key={job.id} value={job.id}>
                    {formatDateShort(job.event_date)} · {jobName(job)}
                  </option>
                ))}
            </Select>
          </div>
        </Field>
        <Field label="Notes" hint="Weddings from before the Studio, what they're good at, anything to remember." className="sm:col-span-2 lg:col-span-3">
          <Textarea rows={2} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center justify-between gap-2 sm:col-span-2 lg:col-span-3">
          <div className="flex gap-2">
            <Button type="submit" variant="primary" loading={save.isPending} disabled={!form.name.trim()}>
              {vendor ? 'Save' : 'Add vendor'}
            </Button>
            <Button variant="ghost" onClick={onDone}>
              Cancel
            </Button>
          </div>
          {vendor ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                if (window.confirm(`Delete ${vendor.name}?`)) remove.mutate(vendor.id, { onSuccess: onDone })
              }}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </form>
    </Section>
  )
}
